/**
 * Data Lake ⇄ Blob view routing (S8 Task 8.9, ruling D1, finding S8-09). Pure over the account's `hns` answer:
 *   - `default` intent (tree, pin, `ui_open_tab`): an HNS account opens a container / prefix in the Data Lake view
 *     (`hnsPath`); a directory-SAS attachment (`hnsPath` root) whose account is NOT confirmed HNS opens as a Blob
 *     prefix (finding S8-R2-05 — `sr=d` is no HNS evidence);
 *   - `blob` intent (the view switch, Open blob view): an `hnsPath` root becomes its Blob-view equivalent;
 *   - `data-lake` intent: a blob-family root becomes `hnsPath` when the account is confirmed HNS.
 * The effective root is resolved BEFORE tab dedupe (`openStorageExplorerTab`).
 */
import { blobViewOfHnsPath, normalizeHnsPath } from '../../../../core/engines/azureStorageHnsPaths';
import type { HnsPathRef, StorageResourceRef } from '../../../../types/azureStorage';

export type StorageViewIntent = 'default' | 'data-lake' | 'blob';
export type AccountHns = 'yes' | 'no' | 'unknown';

/** The Data Lake ref of a container / blob prefix (a directory), or `undefined` when the prefix is not a faithful path. */
export function hnsDirectoryOfBlobRoot(ref: StorageResourceRef): HnsPathRef | undefined {
  if (ref.kind === 'container') return { kind: 'hnsPath', fileSystem: ref.container, path: '', isDirectory: true };
  if (ref.kind !== 'blobPrefix') return undefined;
  const normalized = normalizeHnsPath(ref.prefix, { allowRoot: true });
  return normalized.ok ? { kind: 'hnsPath', fileSystem: ref.container, path: normalized.path, isDirectory: true } : undefined;
}

export function resolveStorageViewRoot(root: StorageResourceRef, intent: StorageViewIntent, hns: AccountHns): StorageResourceRef {
  if (root.kind === 'hnsPath') {
    if (!root.isDirectory) return root;
    if (intent === 'blob' || (intent === 'default' && hns !== 'yes')) return blobViewOfHnsPath(root);
    return root;
  }
  if (hns === 'yes' && (intent === 'default' || intent === 'data-lake')) return hnsDirectoryOfBlobRoot(root) ?? root;
  return root;
}

const dirPath = (ref: HnsPathRef): string => ref.path;

/** True when `location` (any family) names a directory inside the `hnsPath` root. */
function hnsLocationOf(location: StorageResourceRef): HnsPathRef | undefined {
  if (location.kind === 'hnsPath') return location.isDirectory ? location : undefined;
  return hnsDirectoryOfBlobRoot(location);
}

function isInsideHnsRoot(root: HnsPathRef, location: HnsPathRef): boolean {
  if (location.fileSystem !== root.fileSystem) return false;
  return root.path === '' || location.path === root.path || location.path.startsWith(`${dirPath(root)}/`);
}

function isInsideBlobRoot(root: StorageResourceRef, location: StorageResourceRef): boolean {
  if (root.kind === 'container') return (location.kind === 'container' || location.kind === 'blobPrefix') && location.container === root.container;
  if (root.kind === 'blobPrefix') return location.kind === 'blobPrefix' && location.container === root.container && location.prefix.startsWith(root.prefix);
  return false;
}

/** `resource` expressed in the family of `root`; a location outside the root falls back to the root. */
export function mapLocationIntoRoot(root: StorageResourceRef, resource: StorageResourceRef | undefined): StorageResourceRef {
  if (!resource) return root;
  if (root.kind === 'hnsPath') {
    const location = hnsLocationOf(resource);
    return location && root.isDirectory && isInsideHnsRoot(root, location) ? location : root;
  }
  if (root.kind === 'container' || root.kind === 'blobPrefix') {
    const location = resource.kind === 'hnsPath' && resource.isDirectory ? blobViewOfHnsPath(resource) : resource;
    return isInsideBlobRoot(root, location) ? location : root;
  }
  return resource;
}

/** The other view of a tab: its root and the equivalent location, or `undefined` when the tab has no sibling view. */
export function siblingViewTarget(content: { root: StorageResourceRef; resource: StorageResourceRef; mode: string }): { root: StorageResourceRef; resource: StorageResourceRef; viewIntent: 'blob' | 'data-lake' } | undefined {
  if (content.mode === 'hns' && content.root.kind === 'hnsPath' && content.root.isDirectory) {
    const root = blobViewOfHnsPath(content.root);
    return { root, resource: mapLocationIntoRoot(root, content.resource), viewIntent: 'blob' };
  }
  if (content.mode === 'blobs' && (content.root.kind === 'container' || content.root.kind === 'blobPrefix')) {
    const root = hnsDirectoryOfBlobRoot(content.root);
    return root ? { root, resource: mapLocationIntoRoot(root, content.resource), viewIntent: 'data-lake' } : undefined;
  }
  return undefined;
}
